/**
 * ContainerInCanvas — generyczny renderer kontenerów `children[]` w canvasie kreatora
 * jako PRAWDZIWY drop target (parytet z `ColumnsInCanvas` / `AccordionInCanvas` /
 * `StepperInCanvas`). Obsługuje każdy kontener z `LayoutElementRegistry` o
 * `isContainer===true`, który trzyma dzieci w `element.children[]` i NIE ma własnej
 * dedykowanej obsługi (card, row, grid, fieldset, sidebar, collapsible, page-*).
 *
 * Wcześniej te kontenery wpadały do generycznej gałęzi non-field w `SortableSectionView`
 * i renderowały się jako statyczny podgląd BEZ `useDroppable` — przez co pole/element
 * upuszczone „na kontener" trafiało do sekcji-rodzica zamiast do wnętrza. Ten komponent
 * rejestruje własny drop target (`useDroppable` z `data.target='section'` +
 * `sectionId=element.id`, kontrakt z `usePaletteDnD`), więc dispatcher wstawia dziecko
 * do `element.children` przez `insertIntoContainer`.
 *
 * Chrome kontenera (karta / wiersz / siatka / fieldset / …) rysuje jego własna definicja
 * z registry (przez `tryRenderLayoutElement`) — wstrzykujemy do niej ZRENDEROWANE dzieci
 * jako TABLICĘ (nie pojedynczy wrapper), żeby układ poziomy/siatkowy (`row`/`grid`) był
 * zachowany, a placeholdery pustych kontenerów pochodziły 1:1 z runtime renderera.
 * `SortableContext` owija całe chrome — sortable dzieci są jego potomkami niezależnie od
 * struktury DOM. Jeden droppable per kontener (wzorzec SellCall).
 *
 * NIE dla kontenerów o odrębnym modelu dzieci: `accordion` (accordionItems), `stepper`/
 * `wizard` (steps), `tabs` (tabs) mają dedykowane komponenty; `columns` ma `ColumnsInCanvas`.
 */

import { type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import type { LayoutElement } from '@/lib/layout-tree';

export interface ContainerInCanvasProps {
  element: LayoutElement;
  /**
   * Renderuje pojedyncze dziecko kontenera — parent dispatch'uje (field / nested element).
   * `containerId` to id kontenera, używane przez parent do wpięcia `useSortable`
   * z `sortableContainerId={containerId}` (spójne z akordeonem / stepperem / kolumnami).
   */
  renderChild: (child: LayoutElement, containerId: string) => ReactNode;
}

export function ContainerInCanvas({ element, renderChild }: ContainerInCanvasProps): ReactNode {
  const children = element.children ?? [];

  // Kontrakt z usePaletteDnD: kontener jest drop targetem z tym samym shape co sekcja
  // (`target='section'` + `sectionId=element.id`). Dispatcher rozpoznaje generic-container.
  const { setNodeRef, isOver } = useDroppable({
    id: `${element.id}-drop`,
    data: { source: 'section', target: 'section', sectionId: element.id },
  });

  // Dzieci jako TABLICA węzłów — chrome kontenera (row/grid/…) mapuje po niej i zachowuje
  // swój układ (poziomy / siatkowy / pionowy). SortableContext owija całe chrome.
  const childNodes: ReactNode[] = children.map((child) => (
    <div key={child.id}>{renderChild(child, element.id)}</div>
  ));

  const preview = tryRenderLayoutElement({
    element: {
      id: element.id,
      type: element.type,
      settings: (element.settings ?? {}) as Record<string, unknown>,
    },
    children: childNodes,
    mode: 'builder',
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`container-canvas-${element.id}`}
      className={`rounded transition ${
        isOver ? 'bg-accent-50/50 ring-2 ring-dashed ring-accent-400 dark:bg-accent-900/20' : ''
      }`}
    >
      <SortableContext items={children.map((c) => c.id)} strategy={rectSortingStrategy}>
        {preview}
      </SortableContext>
    </div>
  );
}
